const portfolio = [
  {
    slug: 'sentiment-analysis',
    title: 'Sentiment Analysis for Gaming Platform',
    category: 'NLP, Big Data',
    industry: 'Gaming',
    image: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1400&q=80&auto=format&fit=crop',
    summary: 'Real-time sentiment insights from player chat and reviews to improve retention.',
    overview: `We built a streaming pipeline that ingests player chat and reviews, applies NLP models to surface sentiment and themes, and feeds dashboards for product and community teams. The solution reduced churn by identifying early signs of toxicity and dissatisfaction.`,
    challenge: 'High-volume chat and limited labeled data for model training.',
    solution: 'Built a hybrid rules+ML pipeline, used transfer learning to bootstrap models and active learning to refine labels.',
    tech: ['Kafka', 'Spark Streaming', 'Python (NLTK, spaCy)', 'ElasticSearch', 'Grafana'],
    client: 'Mid-size gaming studio',
    duration: '6 months',
    caption: 'Player chat analysis dashboard with live sentiment trends',
    results: ['Real-time dashboards', '20% reduction in churn signals', 'Actionable player segmentation'],
    metrics: { retention: '+35%', accuracy: '89%', volume: '50M+ messages/day' }
  },
  {
    slug: 'predictive-analytics',
    title: 'Predictive Analytics for Retail',
    category: 'Predictive Analytics',
    industry: 'Retail',
    image: 'https://images.unsplash.com/photo-1559526324-593bc073d938?w=1400&q=80&auto=format&fit=crop',
    summary: 'Demand forecasting and inventory optimization for multi-store retailers.',
    overview: `We combined historical sales, promotions, and external signals to forecast demand at SKU-store granularity. The model enabled smarter replenishment and reduced stockouts during peak seasons.`,
    challenge: 'Sparse sales history for new SKUs and seasonality effects.',
    solution: 'Used hierarchical time-series models and external signals (weather, holidays) with confidence intervals for replenishment rules.',
    tech: ['Python', 'Prophet / ARIMA', 'Snowflake', 'Power BI'],
    client: 'Regional retail chain',
    duration: '5 months',
    results: ['15% decrease in stockouts', 'Improved forecast accuracy', 'Automated replenishment rules'],
    metrics: { costReduction: '28%', accuracy: '92%', stores: '150+' }
  },
  {
    slug: 'computer-vision',
    title: 'Computer Vision for Logistics Automation',
    category: 'CV, Automation',
    industry: 'Logistics',
    image: 'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?w=1400&q=80&auto=format&fit=crop',
    summary: 'Automated package inspection and barcode recognition on conveyor lines.',
    overview: `Industrial-grade CV models and edge deployment that identify damaged packages, read labels, and trigger automated sort actions. The system improved throughput and reduced manual inspection costs.`,
    challenge: 'Variable lighting and occluded labels in fast conveyor environments.',
    solution: 'Engineered robust pre-processing, used augmentation and ensembling, and deployed models at the edge with fallback heuristics.',
    tech: ['TensorFlow', 'OpenCV', 'Edge TPU', 'Kubernetes'],
    client: '3PL logistics provider',
    duration: '4 months',
    caption: 'Edge-deployed computer vision inspecting packages on a conveyor',
    results: ['99% barcode read rate', 'Reduced manual inspection by 70%', 'Improved throughput'],
    metrics: { efficiency: '+32%', accuracy: '99%', packages: '5M+/month' }
  },
  {
    slug: 'rag-powered-assistant',
    title: 'RAG-Powered Enterprise Assistant',
    category: 'LLM, GenAI',
    industry: 'Financial Services',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&q=80&auto=format&fit=crop',
    summary: 'AI assistant for internal policy queries using retrieval-augmented generation.',
    overview: `We built a RAG system that indexes internal documentation, compliance manuals, and knowledge bases. Employees query in natural language and receive accurate, cited answers. The solution reduced support ticket volume and improved employee productivity.`,
    challenge: 'Large corpus of unstructured documents, strict accuracy requirements, and need for source attribution.',
    solution: 'Implemented hybrid search with semantic embeddings and keyword matching, added citation tracking, and deployed with human-in-the-loop validation for critical queries.',
    tech: ['OpenAI GPT-4', 'Pinecone', 'LangChain', 'FastAPI', 'React'],
    client: 'Fortune 500 financial services firm',
    duration: '8 months',
    caption: 'RAG assistant interface with source citations and confidence scores',
    results: ['80% reduction in support tickets', 'Average response time: 3 seconds', '95% user satisfaction'],
    metrics: { ticketReduction: '80%', responseTime: '3s', satisfaction: '95%' }
  },
  {
    slug: 'fraud-detection',
    title: 'Real-Time Fraud Detection System',
    category: 'ML, Security',
    industry: 'FinTech',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1400&q=80&auto=format&fit=crop',
    summary: 'ML-powered fraud detection for payment processing platform.',
    overview: `We developed a real-time fraud detection system that analyzes transaction patterns, user behavior, and device fingerprints to identify suspicious activities. The system processes millions of transactions daily with sub-100ms latency while maintaining high precision to minimize false positives.`,
    challenge: 'Extremely imbalanced data (0.1% fraud rate), real-time inference requirements, and evolving fraud patterns.',
    solution: 'Used ensemble methods with SMOTE for class balancing, deployed with feature streaming and model versioning for continuous adaptation to new fraud patterns.',
    tech: ['Python', 'XGBoost', 'Kafka', 'Redis', 'Kubernetes', 'MLflow'],
    client: 'Digital payments platform',
    duration: '7 months',
    caption: 'Real-time fraud detection dashboard with anomaly visualization',
    results: ['95% fraud detection rate', 'False positive rate reduced by 60%', 'Saved $3.2M annually'],
    metrics: { accuracy: '95%', falsePositiveReduction: '60%', savings: '$3.2M/year' }
  },
  {
    slug: 'healthcare-nlp',
    title: 'Clinical Notes Processing System',
    category: 'NLP, Healthcare',
    industry: 'Healthcare',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1400&q=80&auto=format&fit=crop',
    summary: 'NLP pipeline to extract structured data from unstructured clinical notes.',
    overview: `We built an NLP pipeline that extracts diagnoses, medications, procedures, and patient history from free-text clinical notes. The system integrates with EHR systems and supports clinical decision support by surfacing relevant patient information at the point of care.`,
    challenge: 'Medical terminology variability, abbreviations, privacy constraints (HIPAA), and need for high accuracy.',
    solution: 'Fine-tuned BioBERT on clinical data, implemented entity linking to medical ontologies (SNOMED, ICD-10), and deployed with comprehensive audit logging for compliance.',
    tech: ['BioBERT', 'spaCy', 'FHIR', 'AWS HealthLake', 'Python'],
    client: 'Regional hospital network',
    duration: '9 months',
    caption: 'Clinical NLP interface showing entity extraction and medical coding',
    results: ['70% reduction in manual coding time', '93% entity extraction accuracy', 'Improved care coordination'],
    metrics: { timeReduction: '70%', accuracy: '93%', notes: '500K+/month' }
  },
  {
    slug: 'recommendation-engine',
    title: 'Personalized Recommendation Engine',
    category: 'ML, Personalization',
    industry: 'E-commerce',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1400&q=80&auto=format&fit=crop',
    summary: 'Hybrid recommendation system for personalized product discovery.',
    overview: `We designed a multi-stage recommendation system combining collaborative filtering, content-based methods, and contextual bandits. The system provides personalized product recommendations across web, mobile, and email touchpoints, driving significant increases in conversion and average order value.`,
    challenge: 'Cold-start problem for new users/items, diverse product catalog, and need for real-time personalization.',
    solution: 'Implemented hybrid approach with collaborative filtering for active users, content-based for cold-start, and contextual bandits for exploration-exploitation balance.',
    tech: ['Python', 'TensorFlow', 'Redis', 'Apache Airflow', 'BigQuery'],
    client: 'Fashion e-commerce marketplace',
    duration: '6 months',
    caption: 'Personalized recommendation widgets across web and mobile platforms',
    results: ['48% increase in CTR', '23% boost in AOV', 'Improved user engagement metrics'],
    metrics: { ctrIncrease: '48%', aovBoost: '23%', users: '2M+ active' }
  },
  {
    slug: 'telecom-churn',
    title: 'Customer Churn Prediction Platform',
    category: 'Predictive Analytics',
    industry: 'Telecommunications',
    image: 'https://images.unsplash.com/photo-1556740758-90de374c12ad?w=1400&q=80&auto=format&fit=crop',
    summary: 'ML models to predict and prevent customer churn in telecom services.',
    overview: `We developed predictive models that identify customers at risk of churning based on usage patterns, customer service interactions, and demographic data. The insights power targeted retention campaigns and proactive outreach, reducing churn and improving customer lifetime value.`,
    challenge: 'Multiple data sources, complex customer journey, and need for actionable insights at scale.',
    solution: 'Built feature engineering pipeline aggregating behavioral, transactional, and interaction data. Deployed gradient boosting models with SHAP explanations for interpretability.',
    tech: ['Python', 'LightGBM', 'Snowflake', 'Tableau', 'Airflow'],
    client: 'National telecom provider',
    duration: '5 months',
    caption: 'Churn prediction dashboard with customer segmentation and intervention recommendations',
    results: ['89% prediction accuracy', '15% reduction in churn rate', '$12M annual savings'],
    metrics: { accuracy: '89%', churnReduction: '15%', savings: '$12M/year' }
  },
  {
    slug: 'supply-chain-optimization',
    title: 'Supply Chain Optimization Platform',
    category: 'Optimization, AI',
    industry: 'Manufacturing',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1400&q=80&auto=format&fit=crop',
    summary: 'AI-driven supply chain optimization for manufacturing operations.',
    overview: `We built an end-to-end supply chain optimization platform that forecasts demand, optimizes inventory levels, and recommends procurement strategies. The system integrates with ERP systems and uses reinforcement learning to adapt to changing market conditions and supply disruptions.`,
    challenge: 'Multi-echelon supply chain complexity, demand volatility, and supplier reliability issues.',
    solution: 'Implemented hierarchical forecasting with constraint-based optimization and simulation capabilities for scenario planning.',
    tech: ['Python', 'PuLP', 'PyTorch', 'SAP integration', 'Power BI'],
    client: 'Automotive parts manufacturer',
    duration: '10 months',
    caption: 'Supply chain optimization interface with inventory recommendations and scenario analysis',
    results: ['22% reduction in inventory costs', 'Improved supplier collaboration', 'Better demand forecast accuracy'],
    metrics: { costReduction: '22%', accuracy: '91%', skus: '15K+' }
  },
  {
    slug: 'content-moderation',
    title: 'AI Content Moderation System',
    category: 'NLP, CV, Safety',
    industry: 'Social Media',
    image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=1400&q=80&auto=format&fit=crop',
    summary: 'Multi-modal AI system for automated content moderation.',
    overview: `We developed a comprehensive content moderation system that analyzes text, images, and video to detect policy violations including hate speech, harmful content, and misinformation. The system combines multiple AI models with human review workflows to ensure platform safety while maintaining user experience.`,
    challenge: 'Multi-lingual content, contextual understanding, high volume (millions of posts/day), and minimizing over-blocking.',
    solution: 'Built multi-stage pipeline with initial AI triage, confidence scoring, and human review for edge cases. Used active learning to continuously improve models.',
    tech: ['BERT', 'ResNet', 'AWS Rekognition', 'Kubernetes', 'PostgreSQL'],
    client: 'Social networking platform',
    duration: '12 months',
    caption: 'Content moderation dashboard with AI confidence scores and review queue',
    results: ['90% automation rate', '97% policy violation detection', 'Reduced review time by 75%'],
    metrics: { automation: '90%', accuracy: '97%', volume: '10M+ items/day' }
  }
];

export default portfolio;
